import * as angular from 'angular';

import { ComponentDefinition, ContextualField, Miniature, PropertyType, Group, ActionType, TriggerAction } from '../../../models/index';
import { TriggerOrAction, TriggerActionType } from '../TriggerOrAction';
import * as Fields from '../../../common/helpers/contextual/predefinedContextualFields';
import { Steps, Configurations } from '../../../common/Steps';
import { triggerProperties } from './triggerProperties';
import { IEditSendMailActionService } from '../../editSendMailAction/editSendMailAction.service';
import { TriggerActionSelectedItem } from '../TriggerActionSelectedItem';

const stepsIcons =
{
    'hasCleaningInputData': ['fa-eraser', 'fa-tag'],
    'hasCleaningWideResults': ['fa-eraser', 'fa-calculator'],
    'hasAcquisition': 'fa-database',
    'hasScriptComputation': 'fa-code',
    'hasComputation': 'fa-barcode',
    'hasReconciliation': 'fa-caret-square-o-right',
    'hasCalculatedValuesStatuses': 'fa-star-half-o',
    'hasHistorianStorage': 'fa-upload',
    'hasGenerateEvents': 'fa-bullhorn'
};

const configurationsIcons =
{
    'includeDependencies': 'fa-sitemap fa-rotate-180',
    'includeDependent': 'fa-sitemap',
    'includeChildren': 'fa-child',
    'onlyFailed': 'fa-times',
    'onlyCompleted': 'fa-include-only-completed-periods'
};

class TriggerActionContextualController implements angular.IComponentController {
    formController: angular.IFormController;
    properties: ContextualField[];
    miniatures: Miniature[];
    selected: TriggerOrAction;

    readonly groups: Group[];
    readonly selectedItem: TriggerActionSelectedItem;

    static $inject = ['$scope', 'editSendMailActionService'];

    constructor(
        private _$scope: angular.IScope,
        private _editSendMailActionService: IEditSendMailActionService
    ) {
    }

    $onInit() {
        this.miniatures = [
            {
                title: 'Schedule',
                highlights: ['Triggered at regular intervals'],
                group: 'Triggers',
                icon: 'fa-clock-o',
                sortingOrder: 0,
                type: 'scheduleTrigger',
                classed: ['trigger'],
                create: () => { }
            }, {
                title: 'Quality issue',
                highlights: ['Triggered when quality issues are detected'],
                group: 'Triggers',
                icon: 'fa-exclamation-circle',
                sortingOrder: 100,
                type: 'qualityIssueTrigger',
                classed: ['trigger'],
                create: () => { }
            }, {
                title: 'Alarm',
                highlights: ['Triggered when alarms are raised'],
                group: 'Triggers',
                icon: 'fa-bell',
                sortingOrder: 150,
                type: 'alarmTrigger',
                classed: ['trigger'],
                create: () => { }
            }, {
                title: 'Reprocessing task',
                highlights: ['Reprocessing of a group'],
                group: 'Actions',
                icon: 'fa-tasks',
                sortingOrder: 200,
                type: 'reprocessingTaskAction',
                classed: ['action'],
                create: () => { }
            }, {
                title: 'E-mail',
                highlights: ['Send an e-mail', 'You can define dynamic contents of the e-mail and attach dashboards'],
                group: 'Actions',
                icon: 'fa-envelope-o',
                sortingOrder: 300,
                type: 'sendMailAction',
                classed: ['action'],
                create: () => { }
            }
        ];
        this._$scope.$watch(
            () => this.formController,
            () => {
                if (this.formController && this.selected && this.isNewActionOrTrigger(this.selected)) {
                    this.formController.$setDirty();
                }
            }
        );
    }

    private isNewActionOrTrigger(selected: any) {
        // new object if no guid yet and is not a triggeraction link which has both guid
        return !(this.selected as any).guid
            && !(selected.actionGuid && selected.triggerGuid);
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['selectedItem']?.currentValue) {
            if (this.selectedItem.type === TriggerActionType.Trigger) {
                this.properties = triggerProperties[this.selectedItem.trigger.type];
                this.selected = this.selectedItem.trigger;
            } else if (this.selectedItem.type === TriggerActionType.Action) {
                let properties: ContextualField[] = [];
                switch (this.selectedItem.action.type) {
                    case ActionType.ReprocessingTask:
                        properties = this.getReprocessingTaskProperties();
                        break;
                    case ActionType.SendMail:
                        properties = this.getSendMailProperties();
                        break;
                }
                this.properties = properties;
                this.selected = this.selectedItem.action;
            } else if (this.selectedItem.type === TriggerActionType.TriggerActionLink) {
                const properties: ContextualField[] = [
                    {
                        modelName: 'snooze',
                        propertyType: PropertyType.Frequency,
                        iconText: 'Zz',
                        label: 'Snooze time',
                        propertyName: 'snoozeProperty',
                        extraData: {
                            disableFrequencyValidation: true
                        }
                    }
                ];
                this.properties = properties;
                this.selected = this.selectedItem.link;
            } else {
                this.properties = [];
                this.selected = null;
            }

        }
        else
        {
            this.properties = [];
            this.selected = null;
        }
    }

    private getSendMailProperties(): ContextualField[] {
        return this._editSendMailActionService.getProperties();
    }

    private getReprocessingTaskProperties(): ContextualField[] {
        const periods = [
            angular.copy(Fields.MinPeriod),
            angular.copy(Fields.MaxPeriod),
            angular.copy(Fields.PeriodReference)
        ];
        periods.forEach(p => {
            p.extraData['subProperty'] = 'options';
            p.extraData['intervalUnitName'] = 'intervalTimeUnit';
        });

        const properties = [
            angular.copy(Fields.Name),
            {
                label: 'Group',
                modelName: 'groupGuid',
                propertyName: 'groupGuidProperty',
                propertyType: PropertyType.Group,
                icon: 'fa-sitemap',
                extraData: {
                    groups: this.groups,
                    placement: 'left-top'
                },
                messages: [
                    { key: 'required', value: 'A group is required' },
                ]
            },
            angular.copy(Fields.IsDisabledField)
        ]
            .concat(Steps.map(step => {
                return {
                    label: step.label,
                    propertyName: step.propertyName,
                    propertyType: PropertyType.Boolean,
                    modelName: step.propertyName,
                    icon: stepsIcons[step.propertyName],
                    extraData: {
                        subProperty: 'options'
                    }
                };
            }))
            .concat([{
                label: 'Generate events',
                propertyName: 'hasGenerateEventsProperty',
                propertyType: PropertyType.Boolean,
                modelName: 'hasGenerateEvents',
                icon: stepsIcons['hasGenerateEvents'],
                extraData: {
                    subProperty: 'options'
                }
            }])
            .concat(periods)
            .concat(Configurations.map(configuration => {
                return {
                    label: configuration.label,
                    propertyName: configuration.propertyName,
                    propertyType: PropertyType.Boolean,
                    modelName: configuration.propertyName,
                    icon: configurationsIcons[configuration.propertyName],
                    extraData: {
                        subProperty: 'options'
                    }
                };
            }));

        return properties;
    }
}

export const wtTriggerActionContextual: ComponentDefinition = {
    componentId: 'wtTriggerActionContextual',
    controller: TriggerActionContextualController,
    template: require('./triggerActionContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedItem: '<',
        onCancel: '&?',
        onSave: '&',
        formController: '=?',
        groups: '<'
    }
};